<template>
  <div>
    <GenericFilter
      :filter-mode="filterMode"
      :label="$t('campaignDesigner.targetDefinition.dealerCluster.label')"
      :options="mappedOptions"
      :isExP="isExP"
      filter-type="included"
      has-action
      is-multi-picklist
      ref="filter_dealer"
      type="included"
      v-if="editable"
      v-model="localValue">

      <button class="slds-button slds-button_icon" slot="action" @click="openModal" :disabled="!(localValue && localValue.length)">
        <svg class="slds-button__icon" aria-hidden="true">
          <use xlink:href="../../../../assets/symbols.svg#search"></use>
        </svg>
      </button>
    </GenericFilter>

    <OutputField
      :label="$t('campaignDesigner.targetDefinition.dealerCluster.label')"
      :options="mappedOptions"
      :value="localValue"
      type="custom"
      v-else>

      <div class="slds-form-element__static slds-truncate">
        <ul class="slds-list_horizontal slds-has-dividers_right">
          <template v-for="(clusterId, index) in localValue">
            <li class="slds-item" :key="index">
              {{localClusterNames[clusterId]}}
            </li>
          </template>
        </ul>
        <button class="slds-button slds-button_icon" slot="action" @click="openModal" :disabled="!(localValue && localValue.length)">
          <svg class="slds-button__icon" aria-hidden="true">
            <use xlink:href="../../../../assets/symbols.svg#search"></use>
          </svg>
        </button>
      </div>
    </OutputField>

    <transition name="fade">
      <Modal
        @close="closeModal"
        @neutralButtonClick="closeModal"
        @positiveButtonClick="updateDealers"
        :show-footer="editable"
        :title="$t('campaignDesigner.targetDefinition.manageDealers')"
        :neutral-button-text="$t('action.cancel')"
        :positive-button-text="$t('action.save')"
        v-if="showModal">

        <Card
          additional-classes="slds-card_boundary height-reset"
          v-for="(clusterId, index) in Object.keys(tempDealersMap)"
          :key="index"
          :title="localClusterNames[clusterId]">
          <ul class="slds-listbox slds-listbox_horizontal slds-m-top--medium" role="listbox" aria-orientation="horizontal" v-if="tempDealersMap[clusterId] && tempDealersMap[clusterId].length">
            <li class="slds-listbox-item" role="presentation" v-for="(dealer, dealerIndex) in tempDealersMap[clusterId]" :key="dealerIndex">
              <span class="slds-pill" role="option" :tabindex="dealerIndex" aria-selected="true">
                <span class="slds-pill__label">{{localDealersNames[dealer]}}</span>
                <span class="slds-icon_container slds-pill__remove" :title="$t('action.remove')" v-if="editable && tempDealersMap[clusterId].length > 1" @click="removeDealer(clusterId, dealerIndex)">
                  <svg class="slds-icon slds-icon_x-small slds-icon-text-default" aria-hidden="true">
                    <use xlink:href="../../../../assets/symbols.svg#close"></use>
                  </svg>
                </span>
              </span>
            </li>
          </ul>
        </Card>
      </Modal>
    </transition>
  </div>
</template>
<script>
import _ from 'lodash'
import GenericFilter from '../../filters/GenericFilter'
import Card from '../../../slds/Card'
import Modal from '../../../Modal'
import OutputField from '../../../slds/OutputField'
import { getDealerById, getClusterNameByCluster } from '../../../utils/indexedDbUtils'

export default {
  components: {
    Card,
    GenericFilter,
    Modal,
    OutputField
  },
  props: {
    editable: Boolean,
    filterMode: String,
    options: Array,
    selectedDealersMap: Object,
    value: Array,
    isExP: Boolean
  },
  data () {
    return {
      localDealersMap: [],
      localValue: [],
      localClusterNames: {},
      localDealersNames: {},
      tempDealersMap: {},
      showModal: false
    }
  },
  async created () {
    this.localValue = this.value
    this.localDealersMap = this.selectedDealersMap
  },
  computed: {
    mappedOptions () {
      if (this.options) {
        return this.options.map(option => { return { label: option.serviceDescription, value: option.clusterId } })
      } else {
        return []
      }
    }
  },
  methods: {
    async getClusterNameByCluster (clusterId) {
      const cluster = await getClusterNameByCluster(clusterId)
      return { [clusterId]: cluster.serviceDescription }
    },
    async getDealerNameById (dealerId) {
      const dealer = await getDealerById(dealerId)
      return { [dealerId]: dealer.dealer }
    },
    async mapLocalClusterName () {
      if (this.localValue) {
        const names = await Promise.all(this.localValue.map(v => this.getClusterNameByCluster(v)))
        return names.reduce((acc, val) => {
          acc[Object.keys(val)[0]] = Object.values(val)[0]
          return acc
        }, {})
      } else {
        return {}
      }
    },
    async mapLocalDealersName () {
      const objects = new Set()
      if (this.selectedDealersMap) {
        Object.values(this.selectedDealersMap).forEach(values => {
          values.forEach(v => objects.add(v))
        })
        const names = await Promise.all(Array.from(objects).map(v => this.getDealerNameById(v)))
        return names.reduce((acc, val) => {
          acc[Object.keys(val)[0]] = Object.values(val)[0]
          return acc
        }, {})
      } else {
        return []
      }
    },
    closeModal () {
      this.showModal = false
      this.tempDealersMap = {}
    },
    openModal () {
      this.showModal = true
      this.tempDealersMap = _.cloneDeep(this.localDealersMap)
    },
    updateDealers () {
      this.$emit('dealersUpdate', { value: this.tempDealersMap })
      this.closeModal()
    },
    removeDealer (clusterId, dealerIndex) {
      this.tempDealersMap[clusterId].splice(dealerIndex, 1)
    }
  },
  watch: {
    async localValue (value) {
      this.$emit('input', value)
      this.localClusterNames = await this.mapLocalClusterName()
      this.localDealersNames = await this.mapLocalDealersName()
    },
    async selectedDealersMap (selectedDealersMap) {
      this.localDealersMap = selectedDealersMap
    },
    value (value) {
      this.localValue = value
    }
  }
}
</script>
<style lang="scss" scoped>
  .slds-icon_container {
    cursor: pointer;
  }
</style>
